/*
 * Copyright (C) 2019-2022 Corvalent Corporation - All Rights Reserved
 * This software is licensed program property and is Proprietary and Confidential.
 * Unauthorized copying of this file via any medium is strictly prohibited unless
 * prior written permission has been obtained by Corvalent Corporation.
 * In addition, the look and feel of this software as well as the user interface
 * design is also Copyright ©.
 * Notice: This software is not open source.
 * This software is subject to the Corvalent Software License Agreement.
 * You should have received a copy of this license with the software.
 * If not, please write to: sales@corvalent.com
 * All information contained herein is, and remains, the property of
 * Corvalent Corporation and its suppliers, if any. This software may also
 * be covered by U.S. and Foreign Patents, patents in progress, and are
 * protected by trade secret or copyright law
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { ChevronRight, Construction, ExpandMore } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Collapse,
  Divider,
  IconButton,
  ListItemButton,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';

import { closeAnimated } from '../../../../../redux/actions/sideDrawerActions';
import { useRolePermissions } from '../../../../../hooks/useRolePermissions';
import AssetIcon from '../../../../shared/atoms/AssetIcon';
import CustomTooltip from '../../../../shared/atoms/CustomTooltip';
import { useGeneralContext } from '../../../../../context/GeneralContext';
import MoreOptionsMenu from '../../../../shared/molecules/MoreOptionsMenu';
import useDeleteAsset from '../../../../Management/AssetManagement/hooks/useDeleteAsset';
import PopupWrapper from '../../../../Popup/PopupWrapper';
import { statusColorObj } from '../../../../../themes';

const Asset = ({ item, onEdit, parent }) => {
  const history = useHistory();
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width:450px)');
  const { data: userPermissions } = useRolePermissions();
  const dispatch = useDispatch();
  const closeAnimatedAction = useCallback(
    () => dispatch(closeAnimated()),
    [dispatch],
  );
  const { handleDeleteConfirmation } = useDeleteAsset();
  const [popupTooltipOpen, setPopupTooltipOpen] = useState(false);
  const { openBuildings, setOpenBuildings, isMapPage, setClickedAsset } =
    useGeneralContext();

  const itemProps = item.properties || item;

  const isBuildingOpen = useMemo(
    () => openBuildings.includes(itemProps.id),
    [itemProps.id, openBuildings],
  );

  const handleAssetClick = () => {
    let itemWithGeometry = item;
    if (itemProps.type === 'FLR') {
      itemWithGeometry = {
        properties: itemProps,
        geometry: parent.geometry,
      };
    }
    setClickedAsset(itemWithGeometry);
    if (isMapPage && !isBuildingOpen) {
      // Page is "Map"
      window.map.flyTo({
        center: itemWithGeometry.geometry.coordinates,
        zoom: 20,
      });
      window.map.fire('flystart');
    }
    if (itemProps.type === 'BLD')
      setOpenBuildings(prev =>
        prev.includes(itemProps.id)
          ? prev.filter(p => p !== itemProps.id)
          : [...prev, itemProps.id],
      );
    setPopupTooltipOpen(false);
  };

  const goToAssetDetails = useCallback(() => {
    history.push(
      `/portal/maps/facilities/${
        itemProps.floors?.length ? itemProps.floors[0].id : itemProps.id
      }`,
    );
    closeAnimatedAction();
  }, [history, itemProps.floors, itemProps.id, closeAnimatedAction]);

  const goToTickets = assetId => {
    if (!userPermissions?.management?.['service-tickets']?.see) return;
    history.push({
      pathname: '/portal/management/tickets/',
      state: {
        asset_id: assetId,
      },
    });
  };

  const onEditHandler = useCallback(() => {
    if (itemProps.type === 'BLD') {
      onEdit(itemProps.id);
    } else {
      history.push(`/portal/management/assets/${itemProps.id}/manage`);
      closeAnimatedAction();
    }
  }, [closeAnimatedAction, history, itemProps.id, itemProps.type, onEdit]);

  const onClone = useCallback(() => {
    history.push({
      pathname: '/portal/management/assets/new',
      state: itemProps.id,
    });
    closeAnimatedAction();
  }, [closeAnimatedAction, history, itemProps.id]);

  const moreOptions = useMemo(() => {
    let options = [];
    if (userPermissions?.map?.see) {
      options = [
        ...options,
        {
          label: t('components.asset.view_details'),
          action: goToAssetDetails,
        },
      ];
    }
    const isAsset = !itemProps.type;
    if (userPermissions?.map?.manage && !isMobile) {
      options = [
        ...options,
        {
          label: t('buttons.edit'),
          action: onEditHandler,
        },
        ...(isAsset
          ? [
              {
                label: t('buttons.clone'),
                action: onClone,
              },
            ]
          : []),
        ...(isAsset
          ? [
              {
                label: t('buttons.delete'),
                action: () => handleDeleteConfirmation(itemProps),
              },
            ]
          : []),
      ];
    }
    return options;
  }, [
    goToAssetDetails,
    handleDeleteConfirmation,
    isMobile,
    itemProps,
    onClone,
    onEditHandler,
    t,
    userPermissions?.map?.manage,
    userPermissions?.map?.see,
  ]);

  const displayChevron = useMemo(() => {
    if (!itemProps.floors) return null;
    if (isBuildingOpen)
      return <ExpandMore fontSize="small" sx={{ color: 'white' }} />;
    return <ChevronRight fontSize="small" sx={{ color: 'white' }} />;
  }, [isBuildingOpen, itemProps.floors]);

  return (
    <Stack>
      <CustomTooltip
        title={
          !isMobile && (
            <PopupWrapper
              item={itemProps}
              onAction={goToAssetDetails}
              hideDescription
            />
          )
        }
        leaveDelay={100}
        placement="right"
        componentsProps={{
          tooltip: {
            sx: {
              p: 0,
            },
          },
        }}
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, -5],
                },
              },
            ],
          },
        }}
        open={popupTooltipOpen}
        onClose={() => setPopupTooltipOpen(false)}
        onOpen={e => {
          e.target.classList.contains('MuiListItemButton-root') &&
            setPopupTooltipOpen(true);
        }}
      >
        <ListItemButton
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: '8px',
            ':hover': {
              backgroundColor:
                itemProps?.type === 'FLR' ? null : 'secondary.dark',
            },
          }}
          onClick={e => {
            e.preventDefault();
            handleAssetClick();
          }}
        >
          <Stack direction="row" alignItems="center">
            <AssetIcon status={itemProps.status} includeTooltip />
            <Typography
              fontWeight={500}
              sx={{
                maxWidth: isMobile ? '280px' : '190px',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                marginLeft: '1rem',
                color: statusColorObj[(itemProps.status || '').toLowerCase()],
                filter: 'brightness(1.75)',
                textShadow: '1px 1px 1px rgba(0, 0, 0, 0.7)',
              }}
            >
              {itemProps.name}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="flex-end">
            {itemProps.ticketCount > 0 && (
              <CustomTooltip title={t('components.asset.open_tickets')}>
                <IconButton
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    goToTickets(itemProps.id);
                  }}
                  size="small"
                >
                  <Construction
                    data-testid="Construction"
                    sx={{ width: '20px', color: 'white' }}
                  />
                </IconButton>
              </CustomTooltip>
            )}
            <IconButton size="small" aria-label="Show floors">
              {displayChevron}
            </IconButton>
            <MoreOptionsMenu
              options={moreOptions}
              size="small"
              margin="0 -6px 0 0"
            />
          </Stack>
        </ListItemButton>
      </CustomTooltip>
      <Collapse
        in={!!itemProps.floors && isBuildingOpen}
        unmountOnExit
        timeout="auto"
      >
        <Box>
          {itemProps.floors && (
            <Box px="10px">
              <Divider
                sx={{
                  borderColor: theme => theme.palette.primary.main,
                  width: '100%',
                  borderBottomWidth: 1,
                  opacity: 0.5,
                }}
              />
            </Box>
          )}
          <Stack>
            {itemProps.floors?.length === 0 && (
              <Typography
                textAlign="center"
                color={statusColorObj.off}
                py={1}
                fontWeight={500}
                sx={{
                  filter: 'brightness(1.75)',
                  textShadow: '1px 1px 1px rgba(0, 0, 0, 0.7)',
                }}
              >
                {t('components.asset.no_floors')}
              </Typography>
            )}
            {itemProps.floors?.map((floor, index) => (
              <Stack
                key={floor.id}
                paddingLeft="16px"
                sx={{
                  ':hover': {
                    backgroundColor: 'secondary.dark',
                  },
                }}
              >
                <Asset item={floor} parent={item} />
                {index + 1 < itemProps.floors.length && (
                  <Box px="10px">
                    <Divider
                      sx={{
                        borderColor: theme => theme.palette.primary.main,
                        width: '100%',
                        borderBottomWidth: 1,
                        opacity: 0.5,
                      }}
                    />
                  </Box>
                )}
              </Stack>
            ))}
          </Stack>
        </Box>
      </Collapse>
    </Stack>
  );
};

Asset.propTypes = {
  item: PropTypes.shape({
    properties: PropTypes.object,
    geometry: PropTypes.object,
    id: PropTypes.string,
  }).isRequired,
  parent: PropTypes.shape({
    properties: PropTypes.object,
    geometry: PropTypes.object,
    id: PropTypes.string,
  }),
  onEdit: PropTypes.func,
};

export default Asset;
